<template>
  <div
    v-if="displayState !== 'hidden'"
    class="rounded-lg bg-gray-900 px-4 flex flex-col lg:grid lg:grid-rows-[subgrid] lg:row-span-2"
  >
    <!-- Subgrid: the closed card sizes row 1 of the Overview grid together with the stat blocks, the
         reasoning sizes row 2 on its own, so opening it never stretches the blocks beside the card -->
    <div class="py-4 flex flex-col gap-4">
      <!-- Header mirrors StatBlock's title/value stack (text-sm + gap-1 + h-7) so the divider lines up across the row -->
      <div class="flex items-center gap-2">
        <div class="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center shrink-0">
          <img class="w-5 h-5" src="../../../../assets/icons/coach.svg" alt="" />
        </div>
        <div class="flex flex-col items-start gap-1">
          <h2 class="text-text-primary text-sm font-medium">{{ $t('coach.page.title') }}</h2>
          <div class="h-7 flex items-center">
            <Pill class="px-1 py-[1px]" :text="$t('coach.feedback_card.ai_coach')" bgColor="#8A8EDB20" textColor="#8A8EDB" />
          </div>
        </div>
        <div class="flex-1" />
        <Pill class="px-1 py-[1px]" :text="$t('coach.feedback_card.beta')" bgColor="#D071C520" textColor="#D071C5" />
        <Pill
          v-if="displayState === 'locked'"
          class="px-1 py-[1px]"
          :text="$t('coach.feedback_card.competitor_tier')"
          bgColor="#EF6B6B20"
          textColor="#EF6B6B"
        />
        <Info
          :title="$t('coach.feedback_card.info_title')"
          :description="$t('coach.feedback_card.info_description')"
        />
      </div>

      <div class="w-full">
        <div class="w-full h-px bg-gray-800" />
      </div>

      <template v-if="displayState === 'ready'">
        <!-- No flex-1 here: the button's position must not change when the reasoning opens below it -->
        <!-- Annotated (D2): the reasons sit on the phrases they explain, so no toggle -->
        <AnnotatedAdvice v-if="annotated.annotated" class="text-text-secondary text-sm" :segments="annotated.segments" />
        <p v-else class="text-text-secondary text-sm whitespace-pre-line">{{ advice }}</p>
        <template v-if="feedback?.lead">
          <div class="w-full h-px bg-gray-800" />
          <CoachLeadNumber bare :lead="feedback.lead" />
        </template>
        <div class="flex items-center gap-2">
          <button
            v-if="reasoning && !annotated.annotated"
            class="flex items-center gap-2 py-2.5 px-3 bg-gray-800 text-text-primary text-xs cursor-pointer hover:bg-gray-700 duration-300 rounded-md"
            @click="showReasoning = !showReasoning"
          >
            {{ showReasoning ? $t('coach.feedback_card.hide_reasoning') : $t('coach.feedback_card.show_reasoning') }}
            <ChevronDownIcon class="w-4 h-4 duration-300" :class="showReasoning ? 'rotate-180' : ''" />
          </button>
          <router-link
            v-if="SHOW_OPEN_COACH"
            to="/coach"
            class="flex items-center gap-1.5 py-2.5 px-1 text-text-secondary text-xs hover:text-text-primary duration-300"
          >
            {{ $t('coach.feedback_card.open_coach') }}
            <ArrowRightIcon class="w-4 h-4" />
          </router-link>
          <FeedbackThumbs
            v-if="matchId"
            :key="feedback?.feedback_text"
            class="ml-auto"
            :rating="feedback?.rating ?? null"
            :url="`/coach/matches/${matchId}/feedback/rating`"
          />
        </div>
      </template>

      <template v-else-if="displayState === 'locked'">
        <div class="blur-[5px] select-none pointer-events-none flex-1" aria-hidden="true">
          <p class="text-text-secondary text-sm">{{ $t('coach.feedback_card.locked_placeholder') }}</p>
        </div>
        <div class="flex flex-col gap-2">
          <router-link
            to="/upgrade"
            class="py-2.5 px-3 rounded-md bg-primary-600 text-text-primary text-xs font-medium hover:bg-primary-500 duration-300 flex items-center justify-center gap-2"
          >
            {{ $t('coach.feedback_card.upgrade_to_read') }}
            <ArrowRightIcon class="w-4 h-4 text-text-primary" />
          </router-link>
          <span class="text-text-secondary text-xs text-center">{{ upgradeCaption }}</span>
        </div>
      </template>

      <template v-else-if="displayState === 'pending'">
        <div class="flex flex-col gap-2.5 flex-1 animate-pulse" aria-hidden="true">
          <div class="h-3.5 rounded bg-gray-800 w-full" />
          <div class="h-3.5 rounded bg-gray-800 w-[94%]" />
          <div class="h-3.5 rounded bg-gray-800 w-[88%]" />
          <div class="h-3.5 rounded bg-gray-800 w-[42%]" />
        </div>
        <p class="text-text-secondary text-sm" role="status">{{ $t('coach.feedback_card.pending') }}</p>
      </template>

      <template v-else>
        <p class="text-text-secondary text-sm flex-1">{{ $t(`coach.feedback_card.${displayState}`) }}</p>
        <p v-if="requestFailed" class="text-danger-400 text-xs" role="alert">
          {{ $t('coach.feedback_card.request_failed') }}
        </p>
        <div v-if="canRetry" class="flex items-center gap-2">
          <button
            class="py-2.5 px-3 rounded-md bg-primary-600 text-text-primary text-xs font-medium hover:bg-primary-500 duration-300 disabled:cursor-not-allowed disabled:bg-gray-800 disabled:text-text-disabled"
            :disabled="requesting"
            @click="requestFeedback"
          >
            {{ $t('coach.feedback_card.try_again') }}
          </button>
        </div>
      </template>
    </div>

    <div v-if="displayState === 'ready' && reasoning && showReasoning" class="pb-4">
      <div class="bg-gray-800 border border-gray-700 rounded-md p-4 flex flex-col gap-2">
        <h2 class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.feedback_card.reasoning_heading') }}</h2>
        <p class="text-text-secondary text-sm whitespace-pre-line">{{ reasoning }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDownIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';
import Pill from '../../../Pill.vue';
import Info from '../../../General/Info.vue';
import {
  splitFeedback, parseAnnotatedFeedback,
} from '../../../../utils/coach_feedback.js';
import useMatchFeedback from '../../../../composables/useMatchFeedback.js';
import AnnotatedAdvice from '../../AnnotatedAdvice.vue';
import FeedbackThumbs from '../../FeedbackThumbs.vue';
import CoachLeadNumber from '../../sections/CoachLeadNumber.vue';

const { t } = useI18n();

// The locked state shows a blurred stand-in (coach.feedback_card.locked_placeholder):
// the backend never generates feedback for below-tier viewers, so there is no real text to blur.

const props = defineProps({
  // coaching_feedback from /coach/matches/:id: null or { feedback_text, lead, rating }
  feedback: {
    type: Object,
    default: null,
  },
  accessLevel: {
    type: Number,
    default: 0,
  },
  viewerPlayed: {
    type: Boolean,
    default: false,
  },
  // coaching_feedback_state from /coach/matches/:id; undefined from an older backend
  feedbackState: {
    type: String,
    default: undefined,
  },
  // match uuid, for Try again
  matchId: {
    type: String,
    default: null,
  },
});

// Hidden for now, not removed: the Coach page link is coming back once the
// card settles.
const SHOW_OPEN_COACH = false;

const showReasoning = ref(false);

const emit = defineEmits(['requested']);
const {
  display: displayState, canRetry, requesting, requestFailed, requestFeedback,
} = useMatchFeedback(props, emit);

const split = computed(() => splitFeedback(props.feedback?.feedback_text));
const annotated = computed(() => parseAnnotatedFeedback(props.feedback?.feedback_text));
const advice = computed(() => split.value.advice);
const reasoning = computed(() => split.value.reasoning);

const upgradeCaption = computed(() => (props.accessLevel >= 1
  ? t('coach.feedback_card.upgrade_caption_player')
  : t('coach.feedback_card.upgrade_caption_free')));

// A regenerated text (re-parse) should open collapsed again
watch(() => props.feedback?.feedback_text, () => { showReasoning.value = false; });
</script>
